/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { BigNumber as BigNumber_, Order } from '@blade/std-lib'
import {
  CurrencyAddress,
  CurrencyModel,
  currencyRegistry,
} from '@root/entities/currency'
import { PairAddress } from '@root/entities/pair/model'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { reatomPairPriceLatest } from '@root/features/services/pair-price'
import { reatomBigNumber } from '@root/shared/lib/reatom/reatom-big-number'

import type { Network } from '@root/entities/network'
import type { PairModel } from '@root/entities/pair/atoms'
import type { WalletApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

export const reatomWalletAsset = (
  asset: WalletApi.WalletAssetDto,
  network: Network.Network,
  logosHost: TokenLogosHost,
  assetsPairs: Map<PairAddress, PairModel>,
) => {
  const address = CurrencyAddress(asset.address)
  const currency = currencyRegistry.getOrCreate(address, network, () =>
    CurrencyModel.fromWalletAssetDto(asset, logosHost, network),
  )
  const pair = asset.pairAddress
    ? assetsPairs.get(PairAddress(asset.pairAddress))
    : undefined

  const balanceAtom = reatomBigNumber(asset.balance)
  const rawBalanceAtom = atom<string | undefined>(asset.rawBalance ?? undefined)
  const hiddenAtom = atom(asset.hidden)
  const trendAtom = atom(asset.trend)

  const initialPriceUsd = reatomBigNumber(asset.priceUsd)
  const initialPriceNative = reatomBigNumber(asset.priceNative)

  const pairPriceModel = asset.pairAddress
    ? reatomPairPriceLatest({
        pairAddress: PairAddress(asset.pairAddress),
        network: network,
      })
    : undefined

  const priceAtom = atom((ctx) => {
    if (CurrencyModel.isNativeOrWrapped(network)(currency)) {
      const nativePriceAtom = nativePriceByNetwork[network]
      const nativePrice = ctx.spy(nativePriceAtom)
      const priceUsd = nativePrice.isZero()
        ? ctx.spy(initialPriceUsd)
        : nativePrice

      return {
        priceUsd: priceUsd,
        priceNative: BigNumber(1),
      }
    }

    if (!asset.pairAddress || !pairPriceModel) {
      return {
        priceUsd: ctx.spy(initialPriceUsd),
        priceNative: ctx.spy(initialPriceNative),
      }
    }

    const pairPrice = ctx.spy(pairPriceModel)
    return {
      priceUsd: pairPrice?.priceUsd ?? ctx.spy(initialPriceUsd),
      priceNative: pairPrice?.priceNative ?? ctx.spy(initialPriceNative),
    }
  })

  const priceUsdAtom = atom((ctx) => {
    const price = ctx.spy(priceAtom)
    return price.priceUsd
  })

  const priceNativeAtom = atom((ctx) => {
    const price = ctx.spy(priceAtom)
    return price.priceNative
  })

  const balanceUsdAtom = atom((ctx) => {
    const balance = ctx.spy(balanceAtom)
    const priceUsd = ctx.spy(priceUsdAtom)
    return balance.multipliedBy(priceUsd)
  })

  const balanceNativeAtom = atom((ctx) => {
    const priceNative = ctx.spy(priceNativeAtom)
    const balance = ctx.spy(balanceAtom)
    return balance.multipliedBy(priceNative)
  })

  const updateFromDtoAction = action((ctx, dto: WalletApi.WalletAssetDto) => {
    balanceAtom.set(ctx, dto.balance)
    rawBalanceAtom(ctx, dto.rawBalance ?? undefined)
    hiddenAtom(ctx, dto.hidden)
    trendAtom(ctx, dto.trend)
    initialPriceUsd.set(ctx, dto.priceUsd)
    initialPriceNative.set(ctx, dto.priceNative)
    if (dto.pairMeta) {
      pair?.updateFromDto(ctx, dto.pairMeta)
    }
  })

  const toDtoAction = action((ctx): WalletApi.WalletAssetDto => {
    return {
      address: currency.address,
      balance: ctx.get(balanceAtom).toNumber(),
      hidden: ctx.get(hiddenAtom),
      trend: ctx.get(trendAtom),
      decimals: currency.decimals,
      name: currency.title,
      symbol: currency.ticker,
      priceUsd: ctx.get(priceUsdAtom).toNumber(),
      priceNative: ctx.get(priceNativeAtom).toNumber(),
      logo: ctx.get(currency.icon) ?? '',
      pairAddress: asset.pairAddress,
      rawBalance: ctx.get(rawBalanceAtom) ?? undefined,
    }
  })

  return {
    currencyAddress: currency.address,
    currency: currency,
    network,
    hidden: hiddenAtom,
    trend: trendAtom,
    balance: balanceAtom,
    rawBalance: rawBalanceAtom,
    balanceUsd: balanceUsdAtom,
    balanceNative: balanceNativeAtom,
    pairAddress: asset.pairAddress ? PairAddress(asset.pairAddress) : undefined,
    priceUsd: priceUsdAtom,
    priceNative: priceNativeAtom,
    pair,
    updateFromDto: updateFromDtoAction,
    toDto: toDtoAction,
  }
}

export const cmpByBalanceUsd = action((ctx): Order.Order<WalletAssetModel> => {
  return (a, b) => {
    const balanceUsdA = ctx.spy ? ctx.spy(a.balanceUsd) : ctx.get(a.balanceUsd)
    const balanceUsdB = ctx.spy ? ctx.spy(b.balanceUsd) : ctx.get(b.balanceUsd)
    return BigNumber_.order(balanceUsdA, balanceUsdB)
  }
})

const cmpByBalanceUsdDesc = action((ctx) => Order.flip(cmpByBalanceUsd(ctx)))

const cmpByNativeCurrency: Order.Order<WalletAssetModel> = (a, b) => {
  if (CurrencyModel.isNativeOrWrapped(a.currency.network)(a.currency)) {
    return 1
  }
  if (CurrencyModel.isNativeOrWrapped(b.currency.network)(b.currency)) {
    return -1
  }
  return 0
}
const cmpByNativeCurrencyDesc = Order.flip(cmpByNativeCurrency)

const cmpDefault = action((ctx) => {
  return Order.combine(cmpByNativeCurrencyDesc, cmpByBalanceUsdDesc(ctx))
})

reatomWalletAsset.cmpBalanceUsdAsc = cmpByBalanceUsd
reatomWalletAsset.cmpBalanceUsdDesc = cmpByBalanceUsdDesc
reatomWalletAsset.cmpDefault = cmpDefault

export interface WalletAssetModel
  extends ReturnType<typeof reatomWalletAsset> {}
